import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Leaf, Truck } from "lucide-react";
import Seo from "../components/Seo";
import { QuantitySelector } from "../components/QuantitySelector";
import { ProductCard, Stars } from "../components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useCart } from "../context/CartContext";
import { findProduct, formatINR, PRODUCTS } from "../data/products";

const META = (product) => [
  { icon: <Flame size={16} strokeWidth={1.4} />, label: "Burn Time", value: "30–40 hours" },
  { icon: <Leaf size={16} strokeWidth={1.4} />, label: "Wax", value: "Soy–paraffin blend" },
  { icon: <Truck size={16} strokeWidth={1.4} />, label: "Shipping", value: "Free over ₹1,499" },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const product = findProduct(slug);

  if (!product) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 bg-ivory px-6 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Not Found</p>
        <h1 className="font-serif text-4xl text-ink">This candle has not been poured yet.</h1>
        <Link
          to="/candles"
          data-testid="pdp-not-found-link"
          className="border border-cocoa px-8 py-3 text-xs uppercase tracking-[0.25em] text-cocoa transition-colors hover:bg-cocoa hover:text-ivory"
        >
          Shop Candles
        </Link>
      </div>
    );
  }

  const related = PRODUCTS.filter((p) => p.slug !== product.slug).slice(0, 4);

  return (
    <div className="bg-ivory" data-testid="product-detail-page">
      <Seo
        title={`${product.name} — Zelvoria`}
        siteName="ZELVORIA"
        description={product.description}
        image={product.image}
      />
      <div className="mx-auto max-w-7xl px-6 py-12 md:px-10 md:py-16">
        <nav className="text-[10px] uppercase tracking-[0.25em] text-taupe" data-testid="pdp-breadcrumb">
          <Link to="/" className="transition-colors hover:text-gold">Home</Link>
          <span className="mx-2 text-beige">/</span>
          <Link to="/candles" className="transition-colors hover:text-gold">Candles</Link>
          <span className="mx-2 text-beige">/</span>
          <span className="text-ink">{product.fragrance}</span>
        </nav>

        <div className="mt-10 grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <motion.div
              key={activeImg}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="relative aspect-[4/5] overflow-hidden bg-cream"
            >
              <img
                src={product.gallery[activeImg]}
                alt={`${product.name} — ${product.fragrance} scented candle`}
                className="h-full w-full object-cover"
                data-testid="pdp-main-image"
              />
              <div
                className="pointer-events-none absolute inset-0"
                style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(255,242,214,0.14), transparent 55%)" }}
              />
            </motion.div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              {product.gallery.map((g, i) => (
                <button
                  key={`${g}-${i}`}
                  onClick={() => setActiveImg(i)}
                  data-testid={`pdp-thumbnail-${i}`}
                  aria-label={`View image ${i + 1}`}
                  className={`aspect-square overflow-hidden border transition-colors duration-300 ${
                    activeImg === i ? "border-gold" : "border-transparent hover:border-beige"
                  }`}
                >
                  <img src={g} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Scented Luxury Candle</p>
            <h1 className="mt-3 font-serif text-4xl font-light text-ink md:text-5xl" data-testid="pdp-title">
              {product.name}
            </h1>
            <div className="mt-4 flex items-center gap-3">
              <Stars rating={product.rating} />
              <span className="text-xs text-taupe" data-testid="pdp-rating">
                {product.rating} · {product.reviews} reviews
              </span>
            </div>
            <p className="mt-5 text-2xl font-medium tracking-wide text-ink" data-testid="pdp-price">
              {formatINR(product.price)}
            </p>
            <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-taupe">{product.description}</p>

            <div className="mt-8 flex items-stretch gap-4">
              <QuantitySelector qty={qty} onChange={setQty} />
              <button
                onClick={() => add(product.slug, qty)}
                data-testid="pdp-add-to-cart"
                className="flex-1 bg-cocoa text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gold"
              >
                Add to Cart
              </button>
            </div>
            <button
              onClick={() => {
                add(product.slug, qty, true);
                navigate("/checkout");
              }}
              data-testid="pdp-buy-now"
              className="mt-4 w-full border border-gold py-4 text-[11px] uppercase tracking-[0.3em] text-gold transition-colors duration-500 hover:bg-gold hover:text-ivory"
            >
              Buy Now
            </button>

            <div className="mt-8 grid grid-cols-3 gap-4 border-y border-beige py-6 text-center">
              {META(product).map((m) => (
                <div key={m.label} className="flex flex-col items-center gap-2">
                  <span className="text-gold">{m.icon}</span>
                  <span className="text-[9px] uppercase tracking-[0.2em] text-taupe">{m.label}</span>
                  <span className="text-xs text-ink">{m.value}</span>
                </div>
              ))}
            </div>

            <Accordion type="single" collapsible className="mt-4" data-testid="pdp-accordions">
              <AccordionItem value="details">
                <AccordionTrigger data-testid="pdp-accordion-details" className="font-serif text-lg text-ink hover:text-gold hover:no-underline">
                  Details
                </AccordionTrigger>
                <AccordionContent className="text-sm font-light leading-relaxed text-taupe">
                  Hand-poured in small batches. {product.wax}. Approximately 30–40 hours of burn time with a
                  lead-free cotton wick, set in a reusable glass vessel with our ivory label.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="notes">
                <AccordionTrigger data-testid="pdp-accordion-notes" className="font-serif text-lg text-ink hover:text-gold hover:no-underline">
                  Fragrance Notes
                </AccordionTrigger>
                <AccordionContent className="text-sm font-light leading-relaxed text-taupe">
                  <span className="block"><span className="text-gold">Top —</span> {product.notes.top}</span>
                  <span className="mt-1 block"><span className="text-gold">Heart —</span> {product.notes.heart}</span>
                  <span className="mt-1 block"><span className="text-gold">Base —</span> {product.notes.base}</span>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="care">
                <AccordionTrigger data-testid="pdp-accordion-care" className="font-serif text-lg text-ink hover:text-gold hover:no-underline">
                  Candle Care
                </AccordionTrigger>
                <AccordionContent className="text-sm font-light leading-relaxed text-taupe">
                  Trim the wick to 5mm before each burn. Let the first burn melt the wax to the edges — about two
                  hours. Never leave a lit candle unattended, and keep it away from drafts, children and pets.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="shipping">
                <AccordionTrigger data-testid="pdp-accordion-shipping" className="font-serif text-lg text-ink hover:text-gold hover:no-underline">
                  Shipping &amp; Replacement
                </AccordionTrigger>
                <AccordionContent className="text-sm font-light leading-relaxed text-taupe">
                  Dispatched across India in 2–4 business days, in signature Zelvoria gift packaging. Complimentary
                  shipping on orders above ₹1,499. We do not accept returns — however, if your candle arrives
                  damaged, we will gladly replace it. Simply write to us within 48 hours of delivery.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        <div className="mt-28">
          <div className="mb-12 text-center">
            <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Continue the Ritual</p>
            <h2 className="mt-3 font-serif text-3xl font-light text-ink md:text-4xl">You May Also Love</h2>
          </div>
          <div className="grid grid-cols-2 gap-x-5 gap-y-12 md:gap-x-8 lg:grid-cols-4" data-testid="related-products">
            {related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
